import React, { useEffect, useMemo, useState } from "react"
import PropTypes from "prop-types"
import { Dialog, DialogContent, DialogTitle, IconButton, Typography } from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import DuplaConfirmacao from "src/v2/components/DuplaConfirmacao/DuplaConfirmacao"
import theme from "src/v2/theme/theme"
import { COMPANY_REGISTRATION_CONFIG } from "./company-registration-config"
import CompanyRegistrationDetails from "./company-registration-details"
import CompanyRegistrationForm from "./company-registration-form"
import CompanyRegistrationTable from "./company-registration-table"
import useCompanyRegistrationTable from "./use-company-registration-table"

const CompanyRegistrationModal = ({ open, registrationType, companyIdentifier, onClose, onContraparteSaved }) => {
    const definition = COMPANY_REGISTRATION_CONFIG[registrationType]
    const [editor, setEditor] = useState(null)
    const [viewer, setViewer] = useState(null)
    const [rowToDelete, setRowToDelete] = useState(null)
    const shouldRefreshCompany = registrationType === "CONTRAPARTE"
    const handleSaved = useMemo(
        () => (shouldRefreshCompany ? onContraparteSaved : undefined),
        [onContraparteSaved, shouldRefreshCompany]
    )
    const table = useCompanyRegistrationTable({ definition, companyIdentifier, onSaved: handleSaved })

    useEffect(() => {
        setEditor(null)
        setViewer(null)
        setRowToDelete(null)
    }, [companyIdentifier, registrationType])

    if (!definition) return null

    const handleAdd = () => {
        if (!definition.allowCreate) return

        const createPayload = definition.createPayload?.() ?? {}

        setEditor({ isNew: true, row: { ...createPayload, CPFCNPJ: companyIdentifier } })
    }
    const handleEdit = (row) => setEditor({ isNew: false, row })
    const handleConfirmDelete = async () => {
        const deleted = await table.remove(rowToDelete)
        if (deleted) setRowToDelete(null)
    }

    return (
        <>
            <Dialog
                open={open}
                onClose={table.isMutating ? undefined : onClose}
                fullWidth
                maxWidth={false}
                PaperProps={{
                    sx: {
                        m: { xxxs: 0, sm: 2, md: 4 },
                        width: { xxxs: "100vw", sm: "calc(100vw - 32px)", md: "min(1480px, calc(100vw - 64px))" },
                        maxWidth: "none",
                        maxHeight: { xxxs: "100dvh", sm: "calc(100dvh - 32px)" },
                        backgroundColor: theme.palette.background.paper,
                        color: theme.palette.common.white,
                    },
                }}
            >
                <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, px: { xxxs: 2, sm: 3 }, pt: { xxxs: 2, sm: 3 } }}>
                    <Typography variant="h3" color="common.white">{definition.title}</Typography>
                    <IconButton aria-label={`Fechar ${definition.title}`} onClick={onClose} disabled={table.isMutating} sx={{ color: theme.palette.icon.main, "&:focus-visible": { outline: `2px solid ${theme.palette.common.white}`, outlineOffset: 2 } }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ px: { xxxs: 2, sm: 3 }, pb: { xxxs: 2, sm: 3 }, overflow: "hidden" }}>
                    <CompanyRegistrationTable
                        definition={definition}
                        table={table}
                        onAdd={handleAdd}
                        onView={setViewer}
                        onEdit={handleEdit}
                        onDelete={setRowToDelete}
                    />
                </DialogContent>
            </Dialog>

            {editor && (
                <CompanyRegistrationForm
                    open
                    definition={definition}
                    initialRow={editor.row}
                    isNew={editor.isNew}
                    saving={table.isMutating}
                    onClose={() => setEditor(null)}
                    onSave={(payload) => table.save({
                        row: editor.isNew ? payload : { ID: editor.row.ID, ...payload },
                        isNew: editor.isNew,
                    })}
                />
            )}
            {viewer && (
                <CompanyRegistrationDetails
                    open
                    definition={definition}
                    row={viewer}
                    onClose={() => setViewer(null)}
                />
            )}
            <DuplaConfirmacao
                open={Boolean(rowToDelete)}
                title="Excluir registro"
                description="Esta ação não pode ser desfeita. Deseja excluir este registro?"
                cancel={() => setRowToDelete(null)}
                confirm={handleConfirmDelete}
                confirmTextButton={table.isMutating ? "EXCLUINDO..." : "EXCLUIR"}
            />
        </>
    )
}

CompanyRegistrationModal.propTypes = {
    open: PropTypes.bool.isRequired,
    registrationType: PropTypes.oneOf(Object.keys(COMPANY_REGISTRATION_CONFIG)).isRequired,
    companyIdentifier: PropTypes.string,
    onClose: PropTypes.func.isRequired,
    onContraparteSaved: PropTypes.func,
}

CompanyRegistrationModal.defaultProps = {
    companyIdentifier: "",
    onContraparteSaved: undefined,
}

export default CompanyRegistrationModal
